import React, {useState, useEffect, useRef, useCallback, ChangeEvent, FormEvent, Suspense} from "react";
import ReactDOM from "react-dom";
import * as Sentry from "@sentry/browser";
import {IItemContent} from "@connox/typescript-library";
import window from "../lib/WindowExtended";
import {getShopId} from "../lib/getShopId";
import {Trans, useTranslation} from "react-i18next";
import {useMediaQuery} from "react-responsive";
import {useCustomerSelector} from "../../store";
import {BrowserStorage} from "../../storage/BrowserStorage";
import {Button, ButtonStyles, ButtonType} from "../lib/form/Button/Button";
import Image from "../ProductSeries/Image";
import genericStyles from "../lib/GenericStyles.module.scss";
import styles from "./HeaderSearch.module.scss";
import {isoLocales} from "../lib/payments/PaymentController";
import ErrorBoundary from "../utils/ErrorBoundary";

type ResultType = {
  result: {
    totalSearchHits: number;
    itemContent: IItemContent[];
  };
};

type SearchStorageItem = {
  term: string;
  url: string;
};

type SearchStorage = {
  queries: SearchStorageItem[];
};

const initSearchStorage: SearchStorage = {
  queries: [],
};

const browserStorage = new BrowserStorage<SearchStorage>({
  type: "localStorage",
  dataIdentifier: "SearchSuggestions",
  hoursToCache: 48,
});

const HeaderSearchComponent: React.FC = () => {
  const {t} = useTranslation();
  const {customer} = useCustomerSelector();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery({query: "(max-width: 767px)"});

  const submitLink = window.c_links.URL_Search || t("url.searchLink");
  const prevSearchItems = browserStorage.read("prev_search")?.queries;
  const header = document.getElementsByClassName("header-full")[0];
  const menuOpenBtnMobile = document.getElementById("MobileNavButton") as HTMLElement;
  const searchOpenBtn = isMobile
    ? (document.getElementById("HeaderSearchButton") as HTMLElement)
    : (document.getElementById("NavSearchButton") as HTMLElement);
  const searchCloseBtn = isMobile
    ? (document.getElementById("HeaderSearchCloseButton") as HTMLElement)
    : (document.getElementById("NavSearchButtonClose") as HTMLElement);

  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isVisible, setIsVisible] = useState(false);
  const [suggestions, setSuggestions] = useState<IItemContent[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [storedQueries, setStoredQueries] = useState(prevSearchItems || initSearchStorage.queries);
  const [fadeAnimation, setFadeAnimation] = useState("");

  const collectQueryParams = () => {
    let queryParams = `currencyCode=${window.c_ActiveCurrency || "EUR"}`;

    // add url params for correct item prices
    if (customer.customerId) {
      queryParams += `&customerId=${customer.customerId}`;
    }
    if (customer.customerGroupId) {
      queryParams += `&customerGroupId=${customer.customerGroupId}`;
    }
    if (window.c_PromotionId) {
      queryParams += `&promotionId=${window.c_PromotionId}`;
    }

    return queryParams;
  };

  const singleProduct = async (itemIdString: string) => {
    const queryParams = collectQueryParams();
    const getUrl = `${__API_URL__}/${getShopId()}/items?view=content&${queryParams}&itemIds=${itemIdString}`;
    try {
      const headers: HeadersInit = {
        "Content-Type": "application/json",
      };
      if (customer.token) {
        headers.Authorization = `Bearer ${customer.token}`;
      }
      const response = await fetch(getUrl, {
        headers,
      });
      const data = (await response.json()) as {result: IItemContent[]};
      setSuggestions(data.result);
      setTotalResults(data.result.length);
      setLoading(false);
    } catch (err) {
      (err as Error).name = "HeaderSearch" + (err as Error).name;
      Sentry.captureException(err, {extra: {info: "Error while loading item ID search"}});
    }
  };

  // get search suggestions from api (backend)
  const fetchData = async (query: string) => {
    const queryEncoded = encodeURIComponent(query);
    const queryParams = collectQueryParams();
    const getUrl = `${__API_URL__}/${getShopId().toString()}/suggest?${queryParams}&q=${queryEncoded}`;

    try {
      const headers: HeadersInit = {
        "Content-Type": "application/json",
      };
      if (customer.token) {
        headers.Authorization = `Bearer ${customer.token}`;
      }
      const response = await fetch(getUrl, {
        headers,
      });
      const data = (await response.json()) as ResultType;

      setSuggestions(data.result.itemContent);
      setTotalResults(data.result.totalSearchHits);
      setLoading(false);
    } catch (error) {
      (error as Error).name = "HeaderSearch" + (error as Error).name;
      Sentry.captureException(error, {extra: {info: "Error while loading search suggestions"}});
    }
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setSearchTerm(inputValue);
    setLoading(true);
  };

  const clearInput = () => {
    setSearchTerm("");
    inputRef.current?.focus();
  };

  // safe search term in browser storage
  const storePrevSearchItem = (event: FormEvent) => {
    let itemUrl = "";

    if (/^[0-9]{6}$/.test(searchTerm)) {
      event.preventDefault();
      if (suggestions.length && suggestions[0].itemId.toString() === searchTerm) {
        itemUrl = suggestions[0].url;
        window.location.href = itemUrl;
      } else {
        const search = new URLSearchParams(window.location.search);
        search.delete("q");
        search.set("itm", searchTerm);
        itemUrl = window.location.pathname + "?" + search.toString();
        window.location.href = itemUrl;
      }
    }

    if (!storedQueries.find((item) => item.term === searchTerm) && searchTerm !== "") {
      storedQueries?.push({term: searchTerm, url: itemUrl});
    }
    browserStorage.write("prev_search", {
      queries: [...storedQueries],
    });
  };

  // delete previously saved search term from browser storage
  const removePrevSearchItem = (query: string) => {
    setStoredQueries((oldValues) => {
      return oldValues.filter((text) => text.term !== query);
    });
  };

  const submitSearch = (query: string) => {
    const queryEncoded = encodeURIComponent(query);
    window.location.href = `${submitLink}?q=${queryEncoded}`;
  };

  const goToSearchSite = (event: React.MouseEvent<HTMLButtonElement>) => {
    storePrevSearchItem(event);
    submitSearch(searchTerm);
  };

  const searchPrevSearchItem = (query: string) => {
    const storedSearchTerm = storedQueries.find((item) => item.term === query);

    if (/^[0-9]{6}$/.test(query) && storedSearchTerm && storedSearchTerm.url !== "") {
      window.location.href = storedSearchTerm.url;
    } else {
      submitSearch(query);
    }
  };

  const openSearchField = () => {
    if (!isMobile) {
      setFadeAnimation("fadein");
    }

    if (isMobile && !header.classList.contains("open")) {
      header.classList.add("open");
    }

    if (searchOpenBtn && searchCloseBtn) {
      searchOpenBtn.style.display = "none";
      searchCloseBtn.style.display = "flex";
    }

    setIsVisible(true);
  };
  const closeSearchField = () => {
    if (searchOpenBtn && searchCloseBtn) {
      searchCloseBtn.style.display = "none";
      searchOpenBtn.style.display = "flex";
    }

    if (isMobile) {
      setIsVisible(false);
    } else {
      setFadeAnimation("fadeout");
      setTimeout(() => {
        setIsVisible(false);
      }, 300);
    }
  };

  const closeSearchAndHeader = () => {
    closeSearchField();

    if (isMobile && header.classList.contains("open")) {
      header.classList.remove("open");
    }
  };

  const closeOnOutsideClick = useCallback(
    (e) => {
      if (!isMobile && isVisible && containerRef.current && !containerRef.current.contains(e.target)) {
        closeSearchField();
      }
    },
    [isVisible],
  );

  // close search field when user clicks outside of search area (desktop only)
  useEffect(() => {
    document.addEventListener("mousedown", closeOnOutsideClick);
  }, [closeOnOutsideClick]);

  useEffect(() => {
    // load search suggestions when input value has at least 3 characters
    const debounceTimer = setTimeout(() => {
      if (searchTerm.length > 2) {
        if (/^[0-9]{6}$/.test(searchTerm)) {
          void singleProduct(searchTerm);
        } else {
          void fetchData(searchTerm);
        }
      }
    }, 750);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  useEffect(() => {
    if (searchOpenBtn) searchOpenBtn.addEventListener("click", openSearchField);
    if (searchCloseBtn) searchCloseBtn.addEventListener("click", closeSearchAndHeader);
    if (menuOpenBtnMobile) menuOpenBtnMobile.addEventListener("click", closeSearchField);

    // close search field with "esc" key
    document.onkeydown = function (evt) {
      if (evt.code === "Escape") {
        closeSearchField();
      }
    };

    return () => {
      if (searchOpenBtn) searchOpenBtn.removeEventListener("click", openSearchField);
      if (searchCloseBtn) searchCloseBtn.removeEventListener("click", closeSearchAndHeader);
      if (menuOpenBtnMobile) menuOpenBtnMobile.removeEventListener("click", closeSearchField);
    };
  }, []);

  useEffect(() => {
    // prevent 'background' scrolling when search field is visible on mobile devices
    if (isMobile && isVisible) {
      document.body.classList.add(genericStyles.noScroll);
    } else {
      document.body.classList.remove(genericStyles.noScroll);
    }
  }, [isVisible]);

  useEffect(() => {
    browserStorage.write("prev_search", {
      queries: [...storedQueries],
    });
  }, [storedQueries]);

  const PrevSearchItem: React.FC<{term: string}> = ({term}) => {
    return (
      <div className={`${styles.suggestItem} ${styles.suggestItemStored}`}>
        <span
          className={styles.suggestItemStoredLink}
          onClick={() => searchPrevSearchItem(term)}
          title={t("search.searchAgain--query", {query: term})}
          role={"link"}
        >
          <span className={styles.suggestItemStoredIdentifier}>
            <i className="icon icon-clock icon-size--20" />
          </span>
          <span>{term}</span>
        </span>
        <span
          className={styles.suggestItemStoredRemove}
          onClick={() => removePrevSearchItem(term)}
          title={t("general.delete")}
        >
          <i className={`icon icon-close icon-size--22 ${styles.inputButtonRemoveIcon}`} />
        </span>
      </div>
    );
  };

  const SuggestResultItem: React.FC<{item: IItemContent}> = ({item}) => {
    const {title, url, itemId, prices} = item;
    const {advertise, price, retail} = (prices || []).find((prc) => prc.quantity.from === 1) || {};

    return (
      <a
        key={itemId}
        href={url}
        onClick={storePrevSearchItem}
        title={t("general.open--title", {title})}
        className={styles.suggestItem}
      >
        <div className={styles.suggestItemImage}>
          <Image item={item} />
        </div>
        <div className={styles.suggestItemContent}>
          <span className={styles.suggestItemContentTitle}>{title}</span>
          <span>
            {advertise && (
              <>
                <s>{t("general.rrp--price", {price: retail?.formatted})}</s>{" "}
              </>
            )}
            <span className={advertise ? "red" : ""}>{price?.formatted}</span>
            <sup className={genericStyles.footnoteReference}>*</sup>
          </span>
        </div>
        <div className={styles.suggestItemIcon}>
          <i className="icon icon-right icon-size--22" />
        </div>
      </a>
    );
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className={`${styles.wrapper} ${fadeAnimation}`}>
      <div ref={containerRef} className={styles.container}>
        {/* region search input field */}
        <form id="Search" className={styles.input} action={submitLink} onSubmit={storePrevSearchItem} method="get">
          {isMobile && (
            <span
              id="CloseMobileSearchButton"
              className={styles.inputButtonClose}
              role="link"
              onClick={closeSearchAndHeader}
            >
              <i className="icon icon-left icon-size--22">&#160;</i>
            </span>
          )}
          <input
            type="search"
            id="SearchTerm"
            name="q"
            value={searchTerm}
            tabIndex={2}
            ref={inputRef}
            autoFocus={isVisible}
            onChange={onInputChange}
          />
          {searchTerm.length > 0 && (
            <span
              className={styles.inputButtonRemove}
              role="link"
              title={t("search.deleteSearchTerm")}
              onClick={clearInput}
            >
              <i className={`icon icon-close ${styles.inputButtonRemoveIcon}`}>&#160;</i>
            </span>
          )}
          <button id="DropdownSearchButton" className={styles.inputButtonSubmit} type={"submit"}>
            <i className="icon icon-magnifier icon-size--24">&#160;</i>
          </button>
        </form>
        {/* endregion search input field */}

        {/* region previous searches */}
        {storedQueries.length > 0 && searchTerm.length < 3 && (
          <div className={styles.suggest}>
            <div className={styles.suggestHead}>{t("search.recentlySearched")}</div>
            {storedQueries
              .slice(-5)
              .reverse()
              .map(({term}, index) => {
                return <PrevSearchItem key={index} term={term} />;
              })}
          </div>
        )}
        {/* endregion previous searches */}

        {/* region search suggestions */}
        {searchTerm.length > 2 && (
          <div className={styles.suggest}>
            {loading ? (
              <div className={genericStyles.spinnerContainer}>
                <div className="swiper-lazy-preloader">{/* loading spinner container */}</div>
                <br className="clear" />
              </div>
            ) : (
              <>
                <div className={styles.suggestHead}>
                  {totalResults ? (
                    t("product.products--counts", {
                      counts: totalResults.toLocaleString(isoLocales[getShopId()].replace("_", "-")),
                    })
                  ) : (
                    <Trans
                      i18nKey="search.noResultsGiven--query"
                      ns="translations"
                      components={{
                        s: <strong />,
                      }}
                      values={{query: searchTerm}}
                    />
                  )}
                </div>
                {suggestions.slice(0, 5).map((item: IItemContent) => {
                  return <SuggestResultItem key={item.itemId} item={item} />;
                })}
                <div className={styles.suggestButton}>
                  {totalResults > 1 && (
                    <Button action={goToSearchSite} styles={[ButtonStyles.big]} type={ButtonType.default}>
                      {t("search.showAllResults--quantity", {quantity: totalResults.toLocaleString()})}
                    </Button>
                  )}
                </div>
              </>
            )}
          </div>
        )}
        {/* endregion search suggestions */}
      </div>
    </div>
  );
};

const HeaderSearch: React.FC = () => {
  const searchContainer = document.getElementById("HeaderSearchReact");

  if (!searchContainer) {
    return null;
  }

  return ReactDOM.createPortal(
    <ErrorBoundary fallback={null} occurrence="HeaderSearch">
      <Suspense fallback={null}>
        <HeaderSearchComponent />
      </Suspense>
    </ErrorBoundary>,
    searchContainer,
  );
};

export default HeaderSearch;
